import { useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Stack, Step, StepLabel, Stepper } from '@mui/material';

import {
  cancelBatch,
  createBatch,
  deleteFile,
  fetchJob,
  processBatch,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import {
  Batch,
  BatchJobRef,
  FileContext,
  FileContextOverride,
  Job,
  ProblemDetail
} from '../../../types/syntiro_honey';
import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import BatchFileList from './BatchFileList';
import BatchProgressHeader from './BatchProgressHeader';
import ClientField from './ClientField';
import { FileContextFormValue, toFileContext } from './FileContextForm';
import FileDropZone from './FileDropZone';
import JobProgressList from './JobProgressList';
import type { ReviewFileSeed } from '../../../types/syntiro_honey';
import SelectedFileList from './SelectedFileList';
import { isTerminalJobStatus } from './job_status';
import { validateFiles } from './upload_validation';

const JOB_POLL_INTERVAL_MS = 2500;

/**
 * Left-aligns a section's headings against its own form fields.
 *
 * CRA's boilerplate `.App { text-align: center }` (src/App.css) inherits all the way
 * down here, so a `Subheading` centres itself while the MUI inputs under it stay
 * left-aligned. Scoped to this view rather than deleting the global rule, which is
 * load-bearing for other, older views.
 */
const SECTION_SX = { textAlign: 'left' } as const;

/**
 * localStorage key under which an in-flight batch's processing context is stashed,
 * so navigating away from (and back to) the module resumes the progress view instead
 * of losing it — the extraction runs server-side regardless (Maxine #1).
 */
const ACTIVE_BATCH_STORAGE_KEY = 'syntiro_honey_active_batch';

/** The minimum processing context needed to resume polling after a remount. */
interface PersistedProcessing {
  /** The batch being processed (for the cancel action). */
  batchId: string;
  /** One job reference per queued file. */
  jobRefs: Array<BatchJobRef>;
  /** Filename per file id, for the per-file progress rows. */
  filenamesByFileId: Record<string, string>;
  /**
   * File ids deleted from this batch so far (array, not Set — JSON has no Set).
   * Optional so a value persisted before this field existed still parses.
   */
  deletedFileIds?: Array<string>;
}

/** Reads the stashed in-flight batch, or null when absent/unparseable. */
function readPersistedProcessing(): PersistedProcessing | null {
  try {
    const raw = window.localStorage.getItem(ACTIVE_BATCH_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as PersistedProcessing;
    if (typeof parsed.batchId !== 'string' || !Array.isArray(parsed.jobRefs)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Stashes the in-flight batch's processing context for resume-after-navigation. */
function writePersistedProcessing(context: PersistedProcessing): void {
  try {
    window.localStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // A storage failure (quota/private mode) just disables resume — not fatal.
  }
}

/** Clears the stashed in-flight batch once it is finished or abandoned. */
function clearPersistedProcessing(): void {
  try {
    window.localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
  } catch {
    // Ignore — nothing to recover from a failed clear.
  }
}

/** The step of the upload flow the panel is currently in. */
type UploadPhase = 'select' | 'staged' | 'processing' | 'done';

/** Stepper labels, in flow order; the active index mirrors {@link UploadPhase}. */
const FLOW_STEPS: ReadonlyArray<string> = ['Select files', 'Confirm batch', 'Extract', 'Review'];

/** Map the panel phase onto the active stepper index. */
const STEP_INDEX: Record<UploadPhase, number> = {
  select: 0,
  staged: 1,
  processing: 2,
  done: 3
};

interface UploadPanelProps {
  /**
   * Navigate the user to the Review tab once ingestion finishes. The
   * container does not pass this yet, so the button is a safe no-op until
   * it does.
   */
  onGoToReview?: (seed?: ReviewFileSeed) => void;
}

/**
 * Build the per-file context overrides for upload, keeping only files that
 * are still selected and only overrides with at least one non-blank hint.
 * @param {Array<File>} files The files being uploaded.
 * @param {Record<string, FileContextFormValue>} overrides Override form values by filename.
 * @returns {Array<FileContextOverride> | undefined} Overrides, or undefined when none apply.
 */
const buildFileContextOverrides = (
  files: Array<File>,
  overrides: Record<string, FileContextFormValue>
): Array<FileContextOverride> | undefined => {
  const wire_overrides: Array<FileContextOverride> = [];
  for (const file of files) {
    const form_value = overrides[file.name];
    if (form_value === undefined) {
      continue;
    }
    const context: FileContext | undefined = toFileContext(form_value);
    if (context !== undefined) {
      wire_overrides.push({ filename: file.name, context });
    }
  }
  return wire_overrides.length > 0 ? wire_overrides : undefined;
};

/**
 * Filenames whose "replaces an earlier file" override names an earlier file ALREADY
 * claimed by another file in this same selection. Both amendments would diff
 * against the same baseline, so the later submission silently wins and the
 * earlier file's corrections are lost with no error — caught here, before the
 * batch is even created.
 * @param {Array<File>} files The files being uploaded.
 * @param {Record<string, FileContextFormValue>} overrides Override form values by filename.
 * @returns {Array<string>} Filenames whose `supersedesFileId` collides with another file's.
 */
const findSupersedesConflicts = (
  files: Array<File>,
  overrides: Record<string, FileContextFormValue>
): Array<string> => {
  const claimed_by = new Map<string, string>();
  const conflicting = new Set<string>();
  for (const file of files) {
    const target = overrides[file.name]?.supersedesFileId;
    if (target === undefined || target === '') {
      continue;
    }
    const first_claimant = claimed_by.get(target);
    if (first_claimant === undefined) {
      claimed_by.set(target, file.name);
    } else {
      conflicting.add(first_claimant);
      conflicting.add(file.name);
    }
  }
  return Array.from(conflicting);
};

/**
 * Upload step of the Syntiro Honey Data Ingestion view: select files (with the
 * batch-wide client and optional per-file context hints), create the batch,
 * review the persisted file rows, confirm processing, then watch the per-file
 * ingestion jobs until every job reaches a terminal state. One context per file
 * is the whole model — there is no batch-wide hint form (Maxine R4 ④).
 * @param {(() => void) | undefined} onGoToReview Optional navigation callback to the Review tab.
 * @returns {JSX.Element}
 */
export default function UploadPanel({ onGoToReview }: UploadPanelProps) {
  const [phase, setPhase] = useState<UploadPhase>('select');
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [rejectionMessages, setRejectionMessages] = useState<Array<string>>([]);
  // The client every file in the batch belongs to. Auto-assigned (and locked)
  // from the logged-in company by ClientField; sent as the wire batch context so
  // every file inherits it server-side (#44) — including files whose per-file
  // context panel is never opened.
  const [clientCompanyId, setClientCompanyId] = useState<string>('');
  const [overrides, setOverrides] = useState<Record<string, FileContextFormValue>>({});
  const [batch, setBatch] = useState<Batch | null>(null);
  const [jobRefs, setJobRefs] = useState<Array<BatchJobRef>>([]);
  const [jobsById, setJobsById] = useState<Record<string, Job>>({});
  const [creatingBatch, setCreatingBatch] = useState<boolean>(false);
  const [confirmingBatch, setConfirmingBatch] = useState<boolean>(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  // Exact-duplicate files the uploader explicitly confirmed to process as new
  // records (Maxine 2026-08-19 scenario 1). Every EXACT_CONTENT file must be
  // either removed or in this set before the batch can be confirmed.
  const [processAsNewIds, setProcessAsNewIds] = useState<ReadonlySet<string>>(new Set());
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState<boolean>(false);
  const [actionError, setActionError] = useState<ProblemDetail | null>(null);
  const [pollError, setPollError] = useState<ProblemDetail | null>(null);
  const [cancelling, setCancelling] = useState<boolean>(false);
  // The in-flight batch's processing context, kept separate from `batch` so it
  // survives a remount (where `batch` is null but we rehydrate from localStorage).
  const [processing, setProcessing] = useState<{
    batchId: string;
    filenamesByFileId: Record<string, string>;
  } | null>(null);
  const [deletedFileIds, setDeletedFileIds] = useState<ReadonlySet<string>>(new Set());

  // Mirror of jobsById for the polling interval, so each tick sees the
  // latest job states without re-creating the interval on every update.
  const jobsByIdRef = useRef<Record<string, Job>>({});

  // On (re)mount, resume an in-flight batch stashed before the user navigated
  // away: restore the job refs + processing context and re-enter the polling
  // phase, so leaving the page no longer looks like the batch was cancelled
  // (it kept running server-side all along) — Maxine #1.
  useEffect(() => {
    const persisted = readPersistedProcessing();
    if (persisted === null) {
      return;
    }
    jobsByIdRef.current = {};
    setJobsById({});
    setJobRefs(persisted.jobRefs);
    setProcessing({ batchId: persisted.batchId, filenamesByFileId: persisted.filenamesByFileId });
    setDeletedFileIds(new Set(persisted.deletedFileIds ?? []));
    setPhase('processing');
    // Runs once on mount; later phase changes manage their own persistence.
  }, []);

  // Keeps the persisted copy in sync with every delete while a batch is still
  // processing, so a delete made just before navigating away is not lost on
  // return — writePersistedProcessing at batch-start only captures the state as
  // of that moment.
  useEffect(() => {
    if (phase !== 'processing' || processing === null) {
      return;
    }
    writePersistedProcessing({
      batchId: processing.batchId,
      jobRefs,
      filenamesByFileId: processing.filenamesByFileId,
      deletedFileIds: Array.from(deletedFileIds)
    });
  }, [phase, processing, jobRefs, deletedFileIds]);

  const handleFileDeleted = (fileId: string): void => {
    setDeletedFileIds((current) => new Set([...current, fileId]));
  };

  useEffect(() => {
    if (phase !== 'processing' || jobRefs.length === 0) {
      return undefined;
    }
    let cancelled = false;

    const pollOnce = async (): Promise<void> => {
      const pending_refs = jobRefs.filter(
        (ref: BatchJobRef) => !isTerminalJobStatus(jobsByIdRef.current[ref.jobId]?.status)
      );
      if (pending_refs.length === 0) {
        return;
      }
      const results = await Promise.allSettled(
        pending_refs.map((ref: BatchJobRef) => fetchJob(ref.jobId))
      );
      if (cancelled) {
        return;
      }
      const next_jobs: Record<string, Job> = { ...jobsByIdRef.current };
      let last_failure: ProblemDetail | null = null;
      for (const result of results) {
        if (result.status === 'fulfilled') {
          next_jobs[result.value.id] = result.value;
        } else {
          last_failure = toProblemDetail(result.reason);
        }
      }
      jobsByIdRef.current = next_jobs;
      setJobsById(next_jobs);
      setPollError(last_failure);
      const all_terminal = jobRefs.every((ref: BatchJobRef) =>
        isTerminalJobStatus(next_jobs[ref.jobId]?.status)
      );
      if (all_terminal) {
        // The batch reached a terminal state — no more resume needed (#1).
        clearPersistedProcessing();
        setPhase('done');
      }
    };

    void pollOnce();
    const interval_id = window.setInterval(() => void pollOnce(), JOB_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval_id);
    };
  }, [phase, jobRefs]);

  const handleFilesSelected = (candidates: Array<File>): void => {
    const existing_names = selectedFiles.map((file: File) => file.name);
    const { accepted, rejected } = validateFiles(candidates, existing_names);
    if (accepted.length > 0) {
      setSelectedFiles((current) => [...current, ...accepted]);
    }
    setRejectionMessages(rejected);
  };

  const handleRemoveSelected = (filename: string): void => {
    setSelectedFiles((current) => current.filter((file: File) => file.name !== filename));
    setOverrides((current) => {
      const next = { ...current };
      delete next[filename];
      return next;
    });
  };

  const handleOverrideChange = (filename: string, value: FileContextFormValue): void => {
    setOverrides((current) => ({ ...current, [filename]: value }));
  };

  const handleCreateBatch = async (): Promise<void> => {
    setCreatingBatch(true);
    setActionError(null);
    try {
      const created = await createBatch(
        selectedFiles,
        clientCompanyId === '' ? undefined : { companyId: clientCompanyId },
        buildFileContextOverrides(selectedFiles, overrides)
      );
      setBatch(created);
      setPhase('staged');
    } catch (err: unknown) {
      setActionError(toProblemDetail(err));
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleRemoveBatchFile = async (file_id: string): Promise<void> => {
    setRemovingFileId(file_id);
    setActionError(null);
    try {
      await deleteFile(file_id);
      setBatch((current) =>
        current === null
          ? null
          : { ...current, files: current.files.filter((file) => file.id !== file_id) }
      );
      // A removed file needs no acknowledgement any more.
      setProcessAsNewIds((current) => {
        if (!current.has(file_id)) {
          return current;
        }
        const next = new Set(current);
        next.delete(file_id);
        return next;
      });
    } catch (err: unknown) {
      setActionError(toProblemDetail(err));
    } finally {
      setRemovingFileId(null);
    }
  };

  const handleAcknowledgeAsNew = (file_id: string, acknowledged: boolean): void => {
    setProcessAsNewIds((current) => {
      const next = new Set(current);
      if (acknowledged) {
        next.add(file_id);
      } else {
        next.delete(file_id);
      }
      return next;
    });
  };

  const handleProcessBatch = async (): Promise<void> => {
    if (batch === null) {
      return;
    }
    setDuplicateConfirmOpen(false);
    setConfirmingBatch(true);
    setActionError(null);
    try {
      const acknowledged_ids = batch.files
        .filter((file) => processAsNewIds.has(file.id))
        .map((file) => file.id);
      const result = await processBatch(
        batch.batchId,
        acknowledged_ids.length > 0 ? { process_as_new_file_ids: acknowledged_ids } : undefined
      );
      const filenamesByFileId: Record<string, string> = {};
      for (const file of batch.files) {
        filenamesByFileId[file.id] = file.filename;
      }
      jobsByIdRef.current = {};
      setJobsById({});
      setJobRefs(result.jobs);
      setProcessing({ batchId: batch.batchId, filenamesByFileId });
      // The persist-on-change effect below picks this up and stashes it, so
      // navigating away and back resumes the progress view (#1).
      setPhase('processing');
    } catch (err: unknown) {
      setActionError(toProblemDetail(err));
    } finally {
      setConfirmingBatch(false);
    }
  };

  const handleCancelProcessing = async (): Promise<void> => {
    if (processing === null) {
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      // Stops the not-yet-started files; a file already mid-extraction finishes.
      // The poll loop then settles those jobs to CANCELLED and ends the batch (#2).
      await cancelBatch(processing.batchId);
    } catch (err: unknown) {
      setActionError(toProblemDetail(err));
    } finally {
      setCancelling(false);
    }
  };

  const handleStartOver = (): void => {
    clearPersistedProcessing();
    setPhase('select');
    setSelectedFiles([]);
    setRejectionMessages([]);
    setProcessAsNewIds(new Set());
    setDuplicateConfirmOpen(false);
    // The client is deliberately NOT reset: it is auto-assigned from the login
    // (locked) or a session-stable manual choice — clearing it would only force
    // the same pick again on the next batch.
    setOverrides({});
    setBatch(null);
    setJobRefs([]);
    setJobsById({});
    jobsByIdRef.current = {};
    setProcessing(null);
    setDeletedFileIds(new Set());
    setCancelling(false);
    setActionError(null);
    setPollError(null);
  };

  // Prefer the processing context (survives a remount, where `batch` is null);
  // fall back to the freshly-uploaded batch's files in the normal in-session flow.
  const filenames_by_file_id: Record<string, string> = { ...(processing?.filenamesByFileId ?? {}) };
  for (const file of batch?.files ?? []) {
    filenames_by_file_id[file.id] = file.filename;
  }
  const succeeded_count = jobRefs.filter((ref) => jobsById[ref.jobId]?.status === 'DONE').length;
  const failed_count = jobRefs.length - succeeded_count;

  // Scenario-1 gate (Maxine 2026-08-19): every exact-content duplicate must be
  // removed or explicitly acknowledged before the batch can be confirmed.
  const staged_files = batch?.files ?? [];
  const unresolved_duplicates = staged_files.filter(
    (file) => file.duplicate?.kind === 'EXACT_CONTENT' && !processAsNewIds.has(file.id)
  );
  const acknowledged_files = staged_files.filter((file) => processAsNewIds.has(file.id));
  const supersedes_conflicts = findSupersedesConflicts(selectedFiles, overrides);

  const startProcessing = (): void => {
    // The "are you sure" modal fires only when duplicate files are about to be
    // added on purpose; a clean batch keeps its one-click confirm.
    if (acknowledged_files.length > 0) {
      setDuplicateConfirmOpen(true);
      return;
    }
    void handleProcessBatch();
  };

  return (
    <Box sx={{ py: 3 }}>
      <Stepper activeStep={STEP_INDEX[phase]} sx={{ mb: 3 }}>
        {FLOW_STEPS.map((step) => (
          <Step key={step}>
            <StepLabel>{step}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {actionError !== null && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
          <AlertTitle>{actionError.title ?? 'Request failed'}</AlertTitle>
          {actionError.detail}
        </Alert>
      )}

      {phase === 'select' && (
        <Stack spacing={3}>
          <FileDropZone onFilesSelected={handleFilesSelected} disabled={creatingBatch} />
          {rejectionMessages.length > 0 && (
            <Alert severity="warning" onClose={() => setRejectionMessages([])}>
              <AlertTitle>Some files were not added</AlertTitle>
              {rejectionMessages.map((message: string) => (
                <BodyText key={message}>{message}</BodyText>
              ))}
            </Alert>
          )}
          {selectedFiles.length > 0 && (
            <>
              {/* The selection comes FIRST: you have just dropped these files, and
                  being asked to describe them before seeing what you picked put the
                  question ahead of its own subject. Context is PER FILE only, behind
                  each row's "File context" — the batch-wide hint form is gone
                  (Maxine R4 ④); the batch-wide Client stays, below the list. */}
              <Box sx={SECTION_SX}>
                <Subheading weight="Medium" sx={{ mb: 1 }}>
                  {`Selected files (${selectedFiles.length})`}
                </Subheading>
                <SelectedFileList
                  files={selectedFiles}
                  overrides={overrides}
                  onOverrideChange={handleOverrideChange}
                  onRemove={handleRemoveSelected}
                  disabled={creatingBatch}
                />
              </Box>
              <Box sx={SECTION_SX}>
                <ClientField
                  companyId={clientCompanyId}
                  onCompanyIdChange={setClientCompanyId}
                  disabled={creatingBatch}
                />
              </Box>
              <Box sx={{ ...SECTION_SX, display: 'flex', gap: 2, alignItems: 'center' }}>
                <PrimaryButton
                  onClick={() => void handleCreateBatch()}
                  loading={creatingBatch}
                  disabled={supersedes_conflicts.length > 0}
                >
                  Upload
                </PrimaryButton>
                {supersedes_conflicts.length > 0 && (
                  <CaptionText>
                    {`${supersedes_conflicts.join(', ')} name the same earlier file under "Replaces" — give each a different one, or clear all but one.`}
                  </CaptionText>
                )}
              </Box>
            </>
          )}
        </Stack>
      )}

      {phase === 'staged' && batch !== null && (
        <Stack spacing={3}>
          <Subheading weight="Medium">Review uploaded files</Subheading>
          <BodyText>
            These files were uploaded but not yet queued. Remove any you do not want, then confirm
            to start processing.
          </BodyText>
          <BatchFileList
            files={batch.files}
            skipped={batch.skipped}
            onRemoveFile={(id: string) => void handleRemoveBatchFile(id)}
            removingFileId={removingFileId}
            disabled={confirmingBatch}
            acknowledgedFileIds={processAsNewIds}
            onAcknowledgeAsNew={handleAcknowledgeAsNew}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <PrimaryButton
              onClick={startProcessing}
              loading={confirmingBatch}
              disabled={
                batch.files.length === 0 ||
                removingFileId !== null ||
                unresolved_duplicates.length > 0
              }
            >
              Process batch
            </PrimaryButton>
            <PrimaryButton
              palette_color="secondary"
              onClick={handleStartOver}
              disabled={confirmingBatch || removingFileId !== null}
            >
              Start over
            </PrimaryButton>
            {unresolved_duplicates.length > 0 && (
              <CaptionText>
                {`Decide on ${unresolved_duplicates.length} exact duplicate${unresolved_duplicates.length === 1 ? '' : 's'} first: remove the file, or tick "Process as new records".`}
              </CaptionText>
            )}
          </Box>
          <ConfirmationModal
            open={duplicateConfirmOpen}
            title="Add duplicate files as new records?"
            description={`Are you sure you want to add these records which were flagged as duplicates? ${acknowledged_files
              .map((file) => `"${file.filename}"`)
              .join(', ')} will be processed in full, and every row becomes a new record.`}
            confirm_button_text="Process as new records"
            confirm_loading={confirmingBatch}
            handleCloseModal={() => setDuplicateConfirmOpen(false)}
            handleConfirm={() => void handleProcessBatch()}
          />
        </Stack>
      )}

      {(phase === 'processing' || phase === 'done') && (
        <Stack spacing={3}>
          <Subheading weight="Medium">
            {phase === 'processing' ? 'Processing files…' : 'Processing complete'}
          </Subheading>
          {phase === 'processing' && (
            <>
              <BodyText>
                This can take a few minutes for large or complex files. You can leave this page —
                processing continues in the background and your progress is kept.
              </BodyText>
              <Box>
                <PrimaryButton
                  palette_color="secondary"
                  onClick={() => void handleCancelProcessing()}
                  loading={cancelling}
                >
                  Cancel processing
                </PrimaryButton>
              </Box>
            </>
          )}
          {pollError !== null && (
            <Alert severity="warning" onClose={() => setPollError(null)}>
              <AlertTitle>{pollError.title ?? 'Polling problem'}</AlertTitle>
              {`${pollError.detail ?? 'A job-status request failed.'} Polling continues automatically.`}
            </Alert>
          )}
          <BatchProgressHeader jobRefs={jobRefs} jobsById={jobsById} />
          <JobProgressList
            jobRefs={jobRefs}
            jobsById={jobsById}
            filenamesByFileId={filenames_by_file_id}
            deletedFileIds={deletedFileIds}
            onFileDeleted={handleFileDeleted}
            onReviewFile={(seed) => onGoToReview?.(seed)}
          />
          {phase === 'done' && (
            <>
              <Alert severity={failed_count === 0 ? 'success' : 'warning'}>
                <AlertTitle>Batch finished</AlertTitle>
                {`${succeeded_count} file${succeeded_count === 1 ? '' : 's'} succeeded, ${failed_count} failed. Extracted records are waiting in the Review tab.`}
              </Alert>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryButton onClick={() => onGoToReview?.()}>Go to Review</PrimaryButton>
                <PrimaryButton palette_color="secondary" onClick={handleStartOver}>
                  Upload another batch
                </PrimaryButton>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
